<script setup>
import { Link, usePage } from '@inertiajs/vue3'
import {
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    ClockIcon,
    ArrowRightIcon,
} from '@heroicons/vue/24/outline'
import { plumbing, heating, gas } from '@/actions/App/Http/Controllers/ServiceController'
import DemandForm from '@/components/DemandForm.vue'
import { UserIcon } from '@heroicons/vue/24/solid'

const team = [
    {
        name: 'Kalianko Radim',
        role: 'Majitel a jednatel firmy',
        email: 'kalianko@kalianko.cz',
    },
    {
        name: 'Jitka Kalianková',
        role: 'Jednatelka firmy a fakturace',
        email: 'kaliankova@kalianko.cz',
    },
    {
        name: 'Josef Kameník',
        role: 'Obchodní referent',
        email: 'obchod@kalianko.cz',
    },
]

defineOptions({
    inheritAttrs: false,
})

defineProps({
    services: {
        type: Array,
        required: true,
    },
    serviceTypes: {
        type: Array,
        required: true,
    },
})

const seo = {
    title: 'Kontakt',
    description:
        'Kontaktujte firmu Kalianko - nezávazná poptávka na vodoinstalace, topenářství a plynoinstalace. Volejte, pište nebo vyplňte formulář.',
}

const contactInfo = [
    {
        name: 'Telefon',
        description: 'Zavolejte nám pro rychlou konzultaci',
        icon: PhoneIcon,
        value: usePage().props.contacts?.phone || '+420 123 456 789',
        href: 'tel:' + (usePage().props.contacts?.phone || '+420123456789').replace(/\s/g, ''),
    },
    {
        name: 'E-mail',
        description: 'Napište nám vaši poptávku',
        icon: EnvelopeIcon,
        value: usePage().props.contacts?.email || 'info@kalianko.cz',
        href: 'mailto:' + (usePage().props.contacts?.email || 'info@kalianko.cz'),
    },
    {
        name: 'Adresa',
        description: 'Sídlo firmy',
        icon: MapPinIcon,
        value: `${usePage().props.address?.street}, ${usePage().props.address?.zip} ${usePage().props.address?.city}`,
        href: 'https://maps.google.com',
    },
    {
        name: 'Provozní doba',
        description: 'Kdy jsme k dispozici',
        icon: ClockIcon,
        value: 'Po-Pá: 8:00-16:00',
        href: null,
    },
]

const servicesDefault = [
    {
        name: 'Vodoinstalace',
        description: 'Instalace, opravy a servis vodovodních rozvodů',
        route: plumbing(),
    },
    {
        name: 'Topenářství',
        description: 'Montáž a servis topných systémů a kotlů',
        route: heating(),
    },
    {
        name: 'Plynoinstalace',
        description: 'Bezpečné instalace a revize plynových zařízení',
        route: gas(),
    },
]

const benefits = ['Odpovídáme obvykle do 24 hodin', 'Nezávazná konzultace zdarma', 'Havarijní služba 24/7']
</script>

<template>
    <Head :title="seo.title">
        <meta
            name="description"
            :content="seo.description"
        />
    </Head>

    <section class="relative isolate overflow-hidden bg-gray-900 py-24 sm:py-32">
        <div class="from-brand-primary/20 absolute inset-0 -z-10 bg-linear-to-br via-gray-900 to-gray-900" />
        <div
            class="absolute inset-y-0 right-1/2 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-30deg] bg-gray-900/80 shadow-xl ring-1 ring-white/10 sm:mr-28 lg:mr-0 xl:mr-16 xl:origin-center"
        />

        <div class="section-width relative px-2 lg:px-0">
            <div class="mx-auto max-w-2xl text-center">
                <h1 class="text-4xl font-bold tracking-tight text-white sm:text-6xl">Kontaktujte nás</h1>
                <p class="mt-6 text-lg/8 text-gray-300">
                    Máte dotaz nebo potřebujete nezávaznou cenovou nabídku? Neváhejte nás kontaktovat. Rádi vám poradíme
                    a najdeme nejlepší řešení pro váš projekt.
                </p>
            </div>
        </div>
    </section>

    <!-- Contact Cards Section -->
    <section class="section-width px-2 py-16 sm:py-24 lg:px-0">
        <div class="mx-auto max-w-2xl text-center">
            <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Kontaktní údaje</p>
            <h2 class="heading-2 mt-2">Jak nás můžete kontaktovat</h2>
            <p class="paragraph mt-4">
                Vyberte si způsob komunikace, který vám nejvíce vyhovuje. Odpovídáme obvykle do 24 hodin.
            </p>
        </div>

        <div class="mx-auto mt-16 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <a
                v-for="contact in contactInfo"
                :key="contact.name"
                :href="contact.href"
                class="group relative flex flex-col justify-between space-y-5 overflow-hidden rounded-4xl bg-gray-50 p-2 ring-1 ring-gray-100 transition-all hover:bg-white hover:shadow-xl hover:ring-gray-200"
            >
                <div class="p-4">
                    <div
                        class="bg-brand-primary/10 text-brand-primary mb-4 inline-flex size-12 items-center justify-center rounded-xl"
                    >
                        <component
                            :is="contact.icon"
                            class="size-6"
                        />
                    </div>

                    <div>
                        <h3 class="text-lg font-semibold text-gray-900">{{ contact.name }}</h3>
                        <p class="mt-1 text-sm text-gray-500">{{ contact.description }}</p>
                    </div>
                </div>

                <p
                    class="inline-flex w-full items-center justify-center rounded-full border border-sky-500/30 bg-sky-500/10 px-6 py-3 text-sm font-semibold text-sky-500 backdrop-blur-lg transition hover:bg-sky-500/20 sm:w-auto"
                >
                    {{ contact.value }}
                </p>
            </a>
        </div>
    </section>

    <!-- Team Section -->
    <section class="section-width px-2 pb-16 sm:pb-24 lg:px-0">
        <div class="mx-auto max-w-2xl text-center">
            <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Náš tým</p>
            <h2 class="heading-2 mt-2">Zkušení profesionálové</h2>
            <p class="paragraph mt-4">
                Jsme parta zkušených profesionálů s dlouholetou praxí v oboru. Každý z nás se specializuje na konkrétní
                oblast.
            </p>
        </div>

        <div class="mx-auto mt-16 grid max-w-4xl grid-cols-2 gap-8 sm:grid-cols-3">
            <div
                v-for="person in team"
                :key="person.name"
                class="text-center"
            >
                <div
                    class="bg-brand-primary/10 mx-auto flex size-20 items-center justify-center rounded-full sm:size-24"
                >
                    <UserIcon class="text-brand-primary size-10 sm:size-12" />
                </div>
                <h3 class="mt-4 text-base font-semibold text-gray-900">{{ person.name }}</h3>
                <p class="mt-1 text-sm text-gray-600">{{ person.role }}</p>
                <a
                    :href="'mailto:' + person.email"
                    :aria-label="'Napište email na ' + person.email"
                    class="mt-1 text-xs text-sky-500"
                    >{{ person.email }}</a
                >
            </div>
        </div>
    </section>

    <!-- Map & Business information -->
    <section class="bg-gray-50 px-2 lg:px-0">
        <div class="section-width py-16 sm:py-24">
            <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
                <!-- Map -->
                <div class="relative aspect-square overflow-hidden rounded-2xl shadow-lg lg:aspect-4/3">
                    <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2972.0559041299784!2d16.484501736226687!3d48.856431311584764!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4712ca9a01e3e79f%3A0xa8dc0ee658649839!2sKALIANKO%20s.r.o.!5e1!3m2!1scs!2scz!4v1774388268492!5m2!1scs!2scz"
                        width="100%"
                        height="100%"
                        style="border: 0"
                        allowfullscreen=""
                        loading="lazy"
                        referrerpolicy="no-referrer-when-downgrade"
                        class="absolute inset-0"
                    />
                </div>

                <!-- Company Info -->
                <div>
                    <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Informace o firmě</p>
                    <h2 class="heading-2 mt-2">{{ usePage().props.contacts?.company_name }}</h2>
                    <p class="paragraph mt-4">
                        Jsme spolehlivá firma s dlouholetou praxí v oboru instalatérských služeb. Poskytujeme kvalitní
                        služby pro domácnosti i firmy v Drnholci a okolí.
                    </p>

                    <dl class="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <!-- Company Name -->
                        <div class="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
                            <dt class="text-sm text-gray-500">Název společnosti</dt>
                            <dd class="mt-1 text-lg font-semibold text-gray-900">
                                {{ usePage().props.contacts?.company_name }}
                            </dd>
                        </div>

                        <!-- IČO -->
                        <div class="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
                            <dt class="text-sm text-gray-500">IČO</dt>
                            <dd class="mt-1 text-lg font-semibold text-gray-900">
                                {{ usePage().props.contacts?.ico }}
                            </dd>
                        </div>

                        <!-- DIČ -->
                        <div class="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
                            <dt class="text-sm text-gray-500">DIČ</dt>
                            <dd class="mt-1 text-lg font-semibold text-gray-900">
                                {{ usePage().props.contacts?.dic }}
                            </dd>
                        </div>

                        <!-- Address -->
                        <div class="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
                            <dt class="text-sm text-gray-500">Sídlo společnosti</dt>
                            <dd class="mt-1 font-semibold text-gray-900">
                                <p>{{ usePage().props.address?.street }}</p>
                                <p>{{ usePage().props.address?.zip }} {{ usePage().props.address?.city }}</p>
                            </dd>
                        </div>
                    </dl>

                    <div class="mt-10 flex flex-wrap gap-2">
                        <a
                            :href="'tel:' + usePage().props.contacts?.phone?.replace(/\s/g, '')"
                            class="primary-link inline-flex w-full items-center justify-center rounded-full text-base lg:w-fit"
                            :aria-label="'Zavolejte nám na číslo ' + usePage().props.contacts?.phone"
                        >
                            <PhoneIcon class="mr-2 size-5" />
                            Zavolejte nám
                        </a>
                        <a
                            :href="'mailto:' + usePage().props.contacts?.email"
                            class="inline-flex w-full items-center justify-center rounded-full border border-sky-500/30 bg-sky-500/10 px-6 py-3 text-base font-semibold text-sky-500 backdrop-blur-lg transition hover:bg-sky-500/20 sm:w-auto"
                        >
                            <EnvelopeIcon class="mr-2 size-5" />
                            Napište nám
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Services Section -->
    <section class="section-width px-2 py-16 sm:py-24 lg:px-0">
        <div class="mx-auto max-w-2xl text-center">
            <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Naše služby</p>
            <h2 class="heading-2 mt-2">S čím vám můžeme pomoci</h2>
            <p class="paragraph mt-4">Specializujeme se na kompletní instalatérské služby pro domácnosti i firmy.</p>
        </div>

        <div class="mx-auto mt-16 grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-3">
            <Link
                v-for="(service, index) in servicesDefault"
                :key="service.name"
                :href="service.route"
                class="group hover:ring-brand-primary/30 relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
                <div
                    class="text-brand-primary/20 group-hover:text-brand-primary/40 mb-4 text-3xl font-bold transition-colors"
                >
                    {{ String(index + 1).padStart(2, '0') }}
                </div>
                <h3 class="group-hover:text-brand-primary text-lg font-semibold text-gray-900 transition-colors">
                    {{ service.name }}
                </h3>
                <p class="mt-2 text-sm text-gray-600">{{ service.description }}</p>
                <div class="text-brand-primary mt-4 inline-flex items-center text-sm font-semibold">
                    Více informací
                    <ArrowRightIcon class="ml-1 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                </div>
            </Link>
        </div>
    </section>

    <!-- Demand Form Section -->
    <section
        id="demand-form"
        class="bg-gray-100 px-2 lg:px-0"
    >
        <div class="section-width py-16 sm:py-24">
            <div class="mx-auto mb-12 max-w-2xl text-center">
                <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Online poptávka</p>
                <h2 class="heading-2 mt-2">Potřebujete servis nebo opravu?</h2>
                <p class="paragraph mt-4">
                    Vyplňte jednoduchou online poptávku a my se vám ozveme s nezávaznou nabídkou.
                </p>
            </div>

            <DemandForm :services="services" />
        </div>
    </section>
</template>
